import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';

import { EmptyState, Meter, Modal, Panel, Spinner, relativeTime } from '../../components/common/index.jsx';
import { admin } from '../../lib/api.js';
import { useUiStore } from '../../store/uiStore.js';
import { StatusChip } from '../QueuePage.jsx';

export function AssignmentsPage() {
  const [users, setUsers] = useState([]);
  const [batches, setBatches] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [assigning, setAssigning] = useState(false);
  const toast = useUiStore((s) => s.toast);

  const refresh = () => {
    admin.batches().then((r) => setBatches(r.items)).catch(() => setBatches([]));
    admin
      // The server caps at 2000; ask for all of it. One assignment per image, so this
      // holds the whole corpus assigned to everyone at once and cannot truncate silently.
      .assignments({ user_id: selectedUser || undefined, limit: 2000 })
      .then((r) => setAssignments(r.items))
      .catch(() => setAssignments([]));
  };

  useEffect(() => {
    admin.users(false).then((r) => setUsers(r.items)).catch(() => {});
  }, []);
  useEffect(refresh, [selectedUser]);

  if (batches === null) return <Spinner label="Loading assignments…" />;

  return (
    <div className="page wide">
      <div className="page-head row-between">
        <div>
          <h1>Assignments</h1>
          <p>
            Images are assigned whole — all three captions go to the same annotator. Nobody
            gets an image someone else already holds unless you ask for that explicitly.
          </p>
        </div>
        <button type="button" className="btn primary" onClick={() => setAssigning(true)}>
          <Plus size={15} /> Assign a batch
        </button>
      </div>

      <div className="section">
        <Panel
          title="Batches"
          actions={
            <select
              className="select sm"
              style={{ width: 'auto' }}
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
            >
              <option value="">Everyone</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.display_name}
                </option>
              ))}
            </select>
          }
          bodyClass="tight"
        >
          {batches.length === 0 ? (
            <EmptyState title="No batches yet">
              Assign a batch of images to get an annotator started.
            </EmptyState>
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Annotator</th>
                    <th className="num">Images</th>
                    <th className="num">Done</th>
                    <th style={{ minWidth: 120 }}>Progress</th>
                    <th>Strategy</th>
                    <th>By</th>
                  </tr>
                </thead>
                <tbody>
                  {batches
                    .filter((b) => !selectedUser || String(b.user_id) === String(selectedUser))
                    .map((batch) => (
                      <tr key={batch.id}>
                        <td className="tiny faint nowrap">{relativeTime(batch.created_at)}</td>
                        <td>{batch.annotator_display_name}</td>
                        <td className="num">
                          {batch.active_assignments}
                          {batch.granted_count < batch.requested_count && (
                            <span className="chip warn" style={{ marginLeft: 6 }}>
                              asked {batch.requested_count}
                            </span>
                          )}
                        </td>
                        <td className="num">{batch.completed_images}</td>
                        <td>
                          <Meter value={batch.completed_images} total={batch.active_assignments} className="sm" />
                        </td>
                        <td>
                          <span className="chip">{batch.strategy}</span>
                          {batch.allow_overlap ? <span className="chip warn">overlap</span> : null}
                        </td>
                        <td className="tiny faint">{batch.created_by_display_name}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>

      <div className="section">
        <Panel title={`Images (${assignments.length})`} bodyClass="tight">
          {assignments.length === 0 ? (
            <EmptyState title="Nothing assigned">Pick an annotator or assign a batch.</EmptyState>
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Image</th>
                    <th>Set</th>
                    <th>Annotator</th>
                    <th>Status</th>
                    <th className="num">Captions</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {assignments.slice(0, 200).map((a) => (
                    <tr key={a.id}>
                      <td className="num faint">{a.position}</td>
                      <td>
                        <Link to={`/annotate/${a.task_id}?owner=${a.user_id}`}>{a.image_id}</Link>
                      </td>
                      <td>
                        <span className="chip">{a.family === 'coco' ? 'COCO' : 'Web'}</span>
                        {!a.is_exclusive && <span className="chip warn">shared</span>}
                      </td>
                      <td>{a.display_name}</td>
                      <td>
                        <StatusChip status={a.status} />
                      </td>
                      <td className="num">
                        {a.samples_submitted}/{a.samples_total}
                      </td>
                      <td className="right">
                        <button
                          type="button"
                          className="btn sm danger"
                          onClick={async () => {
                            try {
                              await admin.revokeAssignment(a.id);
                              toast('Assignment withdrawn', { tone: 'ok' });
                              refresh();
                            } catch (err) {
                              if (err.code === 'has_work') {
                                const ok = window.confirm(
                                  `This annotator has already worked on ${err.details?.annotated_samples ?? 'some'} caption(s). ` +
                                    'Their work is kept either way. Withdraw the assignment anyway?',
                                );
                                if (ok) {
                                  await admin.revokeAssignment(a.id, true);
                                  refresh();
                                }
                              } else {
                                toast(err.message, { tone: 'danger' });
                              }
                            }
                          }}
                        >
                          Withdraw
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>

      {assigning && (
        <AssignDialog
          users={users}
          onClose={() => setAssigning(false)}
          onDone={(result) => {
            setAssigning(false);
            toast(`Assigned ${result.granted_count} image(s)`, { tone: 'ok' });
            refresh();
          }}
        />
      )}
    </div>
  );
}

function AssignDialog({ users, onClose, onDone }) {
  const [userId, setUserId] = useState(users[0]?.id ?? '');
  const [count, setCount] = useState(100);
  const [family, setFamily] = useState('');
  const [strategy, setStrategy] = useState('balanced');
  const [allowOverlap, setAllowOverlap] = useState(false);
  const [preview, setPreview] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId) return;
    admin
      .availability({ user_id: userId, family: family || undefined })
      .then(setAvailability)
      .catch(() => setAvailability(null));
  }, [userId, family]);

  // Show exactly what will happen before anything is written.
  useEffect(() => {
    if (!userId || !count) return;
    let cancelled = false;
    admin
      .previewAssignment({
        user_id: Number(userId),
        count: Number(count),
        family: family || null,
        strategy,
        allow_overlap: allowOverlap,
      })
      .then((p) => !cancelled && setPreview(p))
      .catch(() => !cancelled && setPreview(null));
    return () => {
      cancelled = true;
    };
  }, [userId, count, family, strategy, allowOverlap]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await admin.createAssignment({
        user_id: Number(userId),
        count: Number(count),
        family: family || null,
        strategy,
        allow_overlap: allowOverlap,
        confirm_overlap: allowOverlap,
        seed: preview?.seed,
      });
      onDone(result);
    } catch (err) {
      setError(err.message || 'Could not assign');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Assign a batch"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={busy || !preview?.would_assign}
            onClick={submit}
          >
            Assign {preview?.would_assign ?? 0} image{preview?.would_assign === 1 ? '' : 's'}
          </button>
        </>
      }
    >
      <div className="field">
        <label htmlFor="assign-user">Annotator</label>
        <select id="assign-user" className="select" value={userId} onChange={(e) => setUserId(e.target.value)}>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.display_name} ({u.role})
            </option>
          ))}
        </select>
      </div>

      <div className="grid-2">
        <div className="field">
          <label htmlFor="assign-count">How many images</label>
          <input
            id="assign-count"
            className="input"
            type="number"
            min="1"
            max="2000"
            value={count}
            onChange={(e) => setCount(e.target.value)}
          />
          {availability && (
            <span className="hint">
              {availability.available_exclusive} unassigned image
              {availability.available_exclusive === 1 ? '' : 's'} available
            </span>
          )}
        </div>
        <div className="field">
          <label htmlFor="assign-family">Which set</label>
          <select id="assign-family" className="select" value={family} onChange={(e) => setFamily(e.target.value)}>
            <option value="">Both COCO and web</option>
            <option value="coco">COCO only</option>
            <option value="web">Web only</option>
          </select>
        </div>
      </div>

      <div className="field">
        <label htmlFor="assign-strategy">Selection</label>
        <select
          id="assign-strategy"
          className="select"
          value={strategy}
          onChange={(e) => setStrategy(e.target.value)}
        >
          <option value="balanced">Balanced — keep the COCO/web ratio in every batch</option>
          <option value="random">Random</option>
          <option value="sequential">Sequential — in corpus order</option>
        </select>
        <span className="hint">
          Balanced avoids giving the first annotators an all-COCO workload, which would skew
          the per-set statistics until the very end.
        </span>
      </div>

      <label className="checkbox">
        <input
          type="checkbox"
          checked={allowOverlap}
          onChange={(e) => setAllowOverlap(e.target.checked)}
        />
        <span>
          <strong>Allow images that are already assigned</strong>
          <br />
          <span className="hint">
            For deliberate double annotation. Off by default; statistics then count each
            sample once unless you ask otherwise.
          </span>
        </span>
      </label>

      {preview && (
        <div className={`banner ${preview.warnings.length ? 'warn' : 'info'}`}>
          <span className="grow">
            Will assign <strong>{preview.would_assign}</strong> image
            {preview.would_assign === 1 ? '' : 's'}
            {Object.keys(preview.by_family).length > 0 && (
              <>
                {' '}(
                {Object.entries(preview.by_family)
                  .map(([f, n]) => `${n} ${f}`)
                  .join(', ')}
                )
              </>
            )}
            .
            {preview.warnings.map((w) => (
              <div key={w}>{w}</div>
            ))}
          </span>
        </div>
      )}

      {error && <div className="banner danger">{error}</div>}
    </Modal>
  );
}
